/**
 * @author mygi
 */
import * as React from 'react';
import { Dialog } from '@shared/ui-react/Dialog';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { Ajax } from '@shared/Ajax';
import { FilesDownload } from '@shared/files/FilesDownload';
import { Message } from 'primereact/message';
import { Notify } from '@shared/ui-native/Notify';

interface IFilesDownloadSelection extends IFilesSelection {
    unpackedSizeBytes: number
}

export const FileFolderDownloadDialog = {
    open: (selection: Partial<IFilesDownloadSelection>) => {
        console.log('FileFolderDownloadDialog selection:', selection);
        if (!kuura.hasPrivilege('Download_Model')) {
            Notify.warn(__('general.validation.access_not_allowed'));
            return;
        }

        ReactUtils.safeRender(<>
            <Dialog onClose={FileFolderDownloadDialog.close} title={__('js_files.download_btn')}>
                <FileFolderDownloadDialogComponent selection={selection}/>
            </Dialog>
        </>, document.getElementById('react-portal-root'));
    },

    close: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
};


@observer
class FileFolderDownloadDialogComponent extends React.Component<{
    selection: Partial<IFilesDownloadSelection>
}> {
    @observable uuid = null;
    @observable onlyFolders = false;
    @observable safeEncoding = false;
    @observable windowsPath = false;
    @observable isStarting = false;
    @observable excludeParentFolders = false;
    @observable errorMsg = null;

    constructor(
        props: any
    ) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
    }

    render() {
        const { selection } = this.props;

        if (this.isStarting) {
            return <div className='overlay-loading' style={{ width: 300, height: 100 }}></div>;
        }

        if (this.errorMsg) {
            return <div className="padded"><Message severity="error" text={this.errorMsg} style={{ width: '100%' }} /></div>;
        }

        const filesSelected = (selection.files || selection.fileUuids)?.length ?? 0;

        if (!this.uuid) {
            return <div className='card padded'>

                <div style={{ alignItems: 'start', padding: '0 7px' }}>
                    {filesSelected ? <div>{__('js_files.files_count')}: {filesSelected}</div> : null}
                    {selection.imageIds?.length ? <div>{__('js_files.images_count')}: {selection.imageIds?.length ?? 0}</div> : null}
                    {selection.folders?.length ? <div>{__('js_files.folders_count')}: {selection.folders?.length ?? 0}</div> : null}
                    {selection.unpackedSizeBytes &&
                        <div>{__('js_files.estimated_uncompressed_size')}: {this.onlyFolders ? 0 : utils.bytesToLargestUnit(selection.unpackedSizeBytes)}</div>
                    }
                </div>
                <hr/>

                <label className='checkbox-label'>
                    <input type='checkbox' checked={this.onlyFolders} onChange={() => this.onlyFolders = !this.onlyFolders}/>
                    <span>{__('js_files.download_only_folders_structure')}</span>
                </label>
                <label className='checkbox-label'>
                    <input type='checkbox' checked={this.safeEncoding} onChange={() => this.safeEncoding = !this.safeEncoding}/>
                    <span>{__('js_files.download_with_safe_encoding')}</span>
                </label>
                <label className='checkbox-label'>
                    <input type='checkbox' checked={this.windowsPath} onChange={() => this.windowsPath = !this.windowsPath}/>
                    <span>{__('js_files.download_with_windows_path')}</span>
                </label>
                <label className='checkbox-label'>
                    <input type='checkbox' checked={this.excludeParentFolders} onChange={() => this.excludeParentFolders = !this.excludeParentFolders}/>
                    <span>{__('js_files.exclude_parent_folders')}</span>
                </label>
                <button className='ik-button-primary' onClick={this.startDownload} style={{ marginTop: 30 }}>{__('js_files.start_download')}</button>
            </div>;
        }

        return (
            <div>
                <FilesDownload uuid={this.uuid}/>
            </div>
        );
    }

    startDownload = () => {
        this.isStarting = true;
        const { selection } = this.props;

        Ajax({
            url: 'api/1/zip/queue_files',
            method: 'POST',
            data: {
                foldersOnly: this.onlyFolders,
                includeImages: true, // files and photos pages are now merged, we want always to download photos inside folders
                safeEncoding: this.safeEncoding,
                windowsPath: this.windowsPath,
                excludeParentFolders: this.excludeParentFolders,
                'folderIds[]': selection.folders,
                'ids[]': selection.files,
                'linkIds[]': selection.shortcuts,
                'imageIds[]': selection.imageIds,
            }
        }).then(resp => {
            this.uuid = resp.uuid;
        }).fail(err => {
            if (Array.isArray(err?.errorMessages)) {
                this.errorMsg = err?.errorMessages[0];
            }
        }).always(() => {
            this.isStarting = false;
        });
    }
}
